import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { get } from "lodash";
import { MenuItem, FocusableItem } from "@szhsin/react-menu";
import { Formik, Form } from "formik";
import {
  ROUTE_GUARDS,
  apiFetch,
  getTotalPages,
  usePermissions,
  formatMonthYear,
} from "utils";
import { useAuthState, useAuthDispatch, useUserState } from "context";
import ActionsMenu from "components/ActionsMenu";
import ConfirmationModal from "components/ConfirmationModal";
import AlertModal from "components/AlertModal";
import Button from "components/Button";
import FormField from "components/FormField";
import Table from "components/Table";
import Pagination from "components/Pagination";

const DATA_ENGAGEMENT_STATUS_LABELS = {
  no_data: "No Data",
  active: "Active",
  lapsed: "Lapsed",
  inactive: "Inactive",
};

const Locations = () => {
  const { token } = useAuthState();
  const dispatch = useAuthDispatch();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const { organization, isAdmin } = useUserState();
  const { orgId } = useParams();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [engagementStatus, setEngagementStatus] = useState("");
  const isOrgMember = permissions[ROUTE_GUARDS.CAN_VIEW_ORG]();

  const getOrgLocations = ({ page = 1, engagementStatus = "" }) =>
    apiFetch({
      token,
      dispatch,
      endpoint: `/api/v1/locations?filter[organization_id][eq]=${orgId}&extra_fields[locations]=us_county_name&page[number]=${page}&stats[total]=count&sort=name${
        engagementStatus
          ? `&filter[data_engagement_status][eq]=${engagementStatus}`
          : ""
      }`,
      includeMeta: true,
    });

  const {
    data: response = { data: [] },
    error,
    isLoading,
  } = useQuery(
    ["organizationLocations", { orgId, page, engagementStatus }],
    () => getOrgLocations({ page, engagementStatus }),
    { keepPreviousData: true }
  );

  useEffect(() => {
    if (totalPages > page) {
      queryClient.prefetchQuery(
        ["organizationLocations", { orgId, page: page + 1, engagementStatus }],
        () => getOrgLocations({ page: page + 1, engagementStatus })
      );
    }
  }, [response, page, queryClient]);

  useEffect(() => {
    const totalPages = getTotalPages({
      totalCount: get(response, "meta.stats.total.count") || 0,
    });
    setTotalPages(totalPages);
  }, [response]);

  const handleSubmit = (values) => {
    if (values.engagementStatus !== engagementStatus) {
      setEngagementStatus(values.engagementStatus);
      setPage(1);
    }
  };

  const columns = [
    {
      accessor: "id",
      Header: "Location Id",
    },
    {
      accessor: "attributes.name",
      Header: "Name",
    },
    {
      accessor: "address",
      Header: "Address",
      Cell: ({ row: { original } }) => (
        <>
          {isOrgMember && `${original.attributes.addressLine1}`}
          {isOrgMember && original.attributes.addressLine2 && (
            <>
              <br />
              {original.attributes.addressLine2}
              <br />
            </>
          )}
          {original.attributes.city},{" "}
          {`${
            original.attributes.country === "United States"
              ? original.attributes.usState
              : original.attributes.provinceOrTerritory
          }, `}
          {original.attributes.country === "United States"
            ? original.attributes.zipCode
            : original.attributes.postalCode}
        </>
      ),
    },
    ...[
      organization?.attributes.country === "United States"
        ? {
            accessor: "attributes.usCountyName",
            Header: "County",
          }
        : {
            accessor: "attributes.provinceOrTerritory",
            Header: "Province/Territory",
          },
    ],
    {
      accessor: "attributes.status",
      Header: "Status",
    },
    ...(isAdmin
      ? [
          {
            accessor: "attributes.lastMonthDataSubmitted",
            Header: "Last Data Submitted",
            Cell: ({ cell }) => formatMonthYear(cell.value),
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            accessor: "attributes.dataEngagementStatus",
            Header: "Engagement Status",
            Cell: ({ cell }) =>
              DATA_ENGAGEMENT_STATUS_LABELS[cell.value] || "—",
          },
        ]
      : []),
  ];

  if (
    permissions[ROUTE_GUARDS.ORGANIZATION_MANAGER]() &&
    permissions[ROUTE_GUARDS.CAN_VIEW_ORG]()
  ) {
    columns.push({
      accessor: "orgId",
      Header: "",
      Cell: ({ row: { original } }) => (
        <Menu
          location={original}
          orgId={orgId}
          triggerDeleteLocationModal={openModal}
        />
      ),
    });
  }

  const openModal = (location) => {
    setLocationToDelete(location);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalError(null);
    setLocationToDelete(null);
  };

  const { mutate: deleteLocation, isLoading: isLoadingDeleteLocation } =
    useMutation(
      ({ id }) =>
        apiFetch({
          token,
          dispatch,
          method: "POST",
          endpoint: "/api/v1/resource_archivers",
          body: {
            data: {
              type: "resource_archivers",
              attributes: {
                resourceId: id,
                resourceType: "location",
              },
            },
          },
        }),
      {
        onSuccess: () => {
          toast.success(
            isAdmin
              ? `Deleted ${locationToDelete.attributes.name} successfully`
              : "SAC administrators have been notified that you would like this location deleted.",
            { autoClose: 3000 }
          );
          queryClient.invalidateQueries(["organizationLocations", { orgId }], {
            refetchActive: true,
          });
          closeModal();
        },
        onError: setModalError,
      }
    );

  return (
    <>
      <div className="mb-8">
        <h2>Locations</h2>

        {permissions[ROUTE_GUARDS.ORGANIZATION_MANAGER]() &&
          permissions[ROUTE_GUARDS.CAN_VIEW_ORG]() && (
            <div className="flex space-x-2">
              <Button
                to={`/organizations/${orgId}/locations/add`}
                data-testid="add-location-btn"
              >
                Add Location
              </Button>
            </div>
          )}
      </div>

      {isAdmin && (
        <div className="sac-border mb-6">
          <Formik
            initialValues={{ engagementStatus: "" }}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue }) => (
              <Form className="flex items-end space-x-6">
                <div className="w-80">
                  <FormField
                    as="select"
                    name="engagementStatus"
                    label="Engagement Status"
                  >
                    <option disabled value="" hidden>
                      -- Engagement Status --
                    </option>
                    <option value="active">Active</option>
                    <option value="lapsed">Lapsed</option>
                    <option value="inactive">Inactive</option>
                    <option value="no_data">No Data</option>
                  </FormField>
                </div>

                <div className="flex items-center space-x-2">
                  <Button type="submit">Filter</Button>
                  <Button
                    onClick={() => {
                      setFieldValue("engagementStatus", "");
                      setEngagementStatus("");
                      setPage(1);
                    }}
                    emphasis="transparent"
                  >
                    Clear Filter
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}

      <Table
        data={response.data || []}
        columns={columns}
        columnWidths={[
          null,
          null,
          null,
          null,
          null,
          ...(isAdmin ? [null] : []),
          ...(isAdmin ? [null] : []),
          "w-16",
        ]}
        columnAlignments={[
          null,
          null,
          null,
          null,
          null,
          ...(isAdmin ? [null] : []),
          ...(isAdmin ? [null] : []),
          "text-right",
        ]}
        noResultsText="No locations found"
        isLoading={isLoading}
        error={error}
      />

      {!!(response.data || []).length && (
        <div className="flex justify-end mt-8">
          <Pagination
            totalPages={totalPages}
            currentPage={page}
            onPageChanged={(page) => setPage(page)}
          />
        </div>
      )}

      <AlertModal isModalOpen={isModalOpen} closeModal={closeModal}>
        <ConfirmationModal
          prompt={`Are you sure you want to delete ${get(
            locationToDelete,
            "attributes.name"
          )}?`}
          isLoading={isLoadingDeleteLocation}
          error={modalError}
          closeModal={closeModal}
          confirmModal={() => deleteLocation(locationToDelete)}
        />
      </AlertModal>
    </>
  );
};

export default Locations;

const Menu = ({ location, orgId, triggerDeleteLocationModal }) => (
  <ActionsMenu>
    <FocusableItem>
      {({ ref }) => (
        <Link to={`/organizations/${orgId}/locations/${location.id}`} ref={ref}>
          View Details
        </Link>
      )}
    </FocusableItem>
    <FocusableItem>
      {({ ref }) => (
        <Link
          to={`/organizations/${orgId}/locations/${location.id}/edit`}
          ref={ref}
        >
          Edit Details
        </Link>
      )}
    </FocusableItem>
    {!location.attributes.primary && (
      <MenuItem onClick={() => triggerDeleteLocationModal(location)}>
        Delete Location
      </MenuItem>
    )}
  </ActionsMenu>
);
